import { forwardRef, useContext, useState } from 'react';
import type { FunctionComponent, PropsWithoutRef, ReactElement, Ref, MouseEvent } from 'react';
import styled from 'styled-components';

import {
  Button,
  Link,
  useConsolidatedRef,
  useElement,
  isValidElement
} from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';
import { getXMLAttributes } from '@pega/cosmos-react-rte';

import { FeedContext } from '../Feed/Feed.context';

interface MentionButtonProps {
  /** Text for the button, or an object containing all necessary props. */
  children: Record<string, string | boolean> | ReactElement;
  /** The id of the mention. */
  id?: string;
  /** The text of the mention button. */
  text?: string;
  /** Designates the type of the mention for proper handling on click. */
  type?: string;
  /** If an href is provided, render a link. Otherwise, render a button. */
  href?: string;
}

const StyledButton = styled(Button)`
  &:enabled:focus-visible,
  &:not([disabled]):focus-visible {
    box-shadow: none;
    text-decoration: underline;
  }
`;

const StyledLink = styled(Link)`
  &:enabled:focus-visible,
  &:not([disabled]):focus-visible {
    box-shadow: none;
    text-decoration: underline;
  }
`;

const serializer = new XMLSerializer();

const MentionButton: FunctionComponent<MentionButtonProps & ForwardProps> = forwardRef(
  function MentionButton(
    {
      children,
      id: idProp = '',
      text: textProp = '',
      type: typeProp = '',
      href: hrefProp = '',
      ...restProps
    }: PropsWithoutRef<MentionButtonProps>,
    ref: Ref<HTMLButtonElement | HTMLAnchorElement>
  ) {
    const { onMentionClick, onMentionPreview } = useContext(FeedContext);
    const [mentionContent, setMentionContent] = useState<JSX.Element | undefined>();
    const [target, setTarget] = useElement<HTMLButtonElement | HTMLAnchorElement>(null);
    const mentionRef = useConsolidatedRef(ref);

    let id = '';
    let text = '';
    let type = '';
    let href = '';
    let bold = false;
    let italic = false;
    let lineThrough = false;
    let code = false;

    if (!isValidElement(children)) {
      if (!children.id || !children.text || !children.type) return <span>{children.rawText}</span>;
      id = String(children.id);
      text = String(children.text);
      type = String(children.type);
      href = String(children.href || '');
      bold = !!children.bold;
      italic = !!children.italic;
      lineThrough = !!children['line-through'];
      code = !!children.code;
    } else {
      id = idProp;
      text = textProp;
      type = typeProp;
      href = hrefProp;
    }

    let formattedText: string | JSX.Element = text;
    if (code) formattedText = <code>{formattedText}</code>;
    if (lineThrough) formattedText = <del>{formattedText}</del>;
    if (italic) formattedText = <em>{formattedText}</em>;
    if (bold) formattedText = <strong>{formattedText}</strong>;

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      setTarget(e.currentTarget);
      const comp = onMentionClick?.({
        id,
        text,
        type,
        target: e.currentTarget,
        closeGlimpse: () => setTarget(null)
      });
      if (comp) setMentionContent(comp);
    };

    return (
      <>
        {href ? (
          <StyledLink
            {...restProps}
            ref={mentionRef as Ref<HTMLAnchorElement>}
            href={href}
            previewable={!!onMentionPreview}
            onPreview={
              onMentionPreview
                ? () =>
                    onMentionPreview({
                      id,
                      text,
                      type
                    })
                : undefined
            }
            spellCheck={false}
          >
            {isValidElement(children) ? children : formattedText}
          </StyledLink>
        ) : (
          <StyledButton
            {...restProps}
            ref={mentionRef}
            variant='link'
            onClick={handleClick}
            spellCheck={false}
          >
            {isValidElement(children) ? children : formattedText}
          </StyledButton>
        )}
        {target && mentionContent}
      </>
    );
  }
);

export const MentionButtonConfig = {
  type: 'mention',
  xmlElement: 'pega-mention',
  regexPattern: /(?:^|)<pega-mention (?:[^\n/]|\/(?!>))+\/>/g,
  component: MentionButton,
  inject: ({ id, type, text, href }: { id: string; type: string; text: string; href?: string }) => {
    if (!id || !type || !text) return;

    const element = document.createElementNS(null, 'pega-mention');
    element.setAttribute('id', id);
    element.setAttribute('text', text);
    element.setAttribute('type', type);
    if (href) element.setAttribute('href', href);

    return serializer.serializeToString(element);
  },
  extract: (element: string) => {
    const attributes = getXMLAttributes(element);

    if (!attributes || !attributes.id || !attributes.text || !attributes.type) return undefined;
    return {
      id: attributes.id,
      text: attributes.text,
      type: attributes.type,
      href: attributes.href
    };
  }
};

export default MentionButton;
